<template>
	<div v-if="propertyTypeRequirementsMet">
		<div class="editor">
			<el-button
				:id="'wf-tour-make-variable-btn-' + property.name"
				class="wf-tour-make-variable-btn"
				@click="
					() => {
						$emit('inputIsVariable', !inputIsVariable);
					}
				"
				style="display: flex; margin-left: auto; padding-top: 0px"
				type="text"
			>
				Make input {{ !inputIsVariable ? "variable" : "constant" }}
			</el-button>
			<el-button
				v-if="inputIsVariable"
				@click="insertCodeBlock"
				style="display: flex; margin-left: auto; padding-top: 0px; font-size: 10px"
				type="text"
			>
				Insert code expression
			</el-button>
			<editor-content v-if="inputIsVariable" class="editor__content" :editor="editor" />
			<p
				v-if="inputIsVariable"
				style="font-size: 10px; word-break: break-word; margin: 0px; line-height: 20px; margin-top: 5px"
			>
				Variable inputs can reference keys from incoming data
			</p>
		</div>
	</div>
</template>

<script>
import Document from "@tiptap/extension-document";
import Mention from "@tiptap/extension-mention";
import Paragraph from "@tiptap/extension-paragraph";
import History from "@tiptap/extension-history";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { all, createLowlight } from "lowlight";
const lowlight = createLowlight(all);
import { Editor, EditorContent } from "@tiptap/vue-2";
import { mapGetters, mapActions } from "vuex";
import { v4 as uuidv4 } from "uuid";
//Support for
//String
//Boolean
//Single Select
//Single Select with search
//Numbers
export default {
	computed: {
		...mapGetters(["returnReferencePath", "returnLastFocusedVariableInsertionInput"]),
		propertyTypeRequirementsMet() {
			const type = this.property.type;
			if (
				this.integrationInterface.type !== "webhook" &&
				this.integrationInterface.type !== "trigger" &&
				((type === "string" &&
					!this.property.typeOptions?.multipleValues &&
					!this.property.typeOptions?.editor) ||
					type === "boolean" ||
					type === "number" ||
					type === "dateTime" ||
					(type === "options" &&
						this.property.name !== "resource" &&
						this.property.name !== "operation" &&
						this.property.name !== "authentication"))
			) {
				return true;
			} else {
				return false;
			}
		},
	},
	components: {
		EditorContent,
	},
	props: {
		integrationInterface: Object,
		inputIsVariable: Boolean,
		property: Object,
		content: {
			default: "",
		},
	},
	watch: {
		returnReferencePath: function () {
			if (this.returnLastFocusedVariableInsertionInput === this.componentId) {
				this.addVariable(this.returnReferencePath.path);
			}
		},
	},
	data() {
		return {
			componentId: uuidv4(),
			editor: new Editor({
				onTransaction: (props) => {
					this.lastCursorPosition = props.transaction.curSelection.$anchor.pos;
				},
				onUpdate: (props) => {
					let content = props.editor.getJSON();
					this.updateProp(content);
				},
				onFocus: () => {
					this.updateLastFocusedVariableInsertionInputMETHOD(this.componentId);
				},
				extensions: [
					CodeBlockLowlight.configure({
						lowlight,
						defaultLanguage: "javascript",
					}),
					HardBreak,
					Document,
					Paragraph,
					History,
					Text,
					Mention.configure({
						HTMLAttributes: {
							class: "mention",
						},
						renderLabel({ node }) {
							return decodeURI(node.attrs.id);
						},
						suggestion: {
							char: "",
						},
					}),
				],
				content: this.highlightVariables(this.content),
			}),
			lastCursorPosition: 1,
		};
	},
	methods: {
		...mapActions(["updateLastFocusedVariableInsertionInputMETHOD"]),
		insertCodeBlock() {
			const defaultCode = `// Built-in $inputDataObject, $inputDataIndex and log();
// Primitive return values are inserted into the string 
return $inputDataObject.json.firstName + ' ' + '{{json.lastName}}';`;
			this.editor.commands.insertContentAt(
				this.lastCursorPosition,
				`<pre><code class="language-js">${defaultCode}</code></pre>`,
				{
					updateSelection: true,
					parseOptions: {
						preserveWhitespace: "full",
					},
				}
			);
		},
		addVariable(variable) {
			const resolvedPos = this.editor.state.doc.resolve(this.lastCursorPosition);
			let insideCodeBlock = false;
			resolvedPos.path.forEach((node) => {
				if (node.type?.name === "codeBlock") {
					insideCodeBlock = true;
				}
			});
			if (insideCodeBlock) {
				this.editor.commands.insertContentAt(this.lastCursorPosition, `{{${variable}}}`, {
					updateSelection: true,
					parseOptions: {
						preserveWhitespace: "full",
					},
				});
			} else {
				this.editor.commands.insertContentAt(
					this.lastCursorPosition,
					`<span data-type="mention" class="mention" data-id="{{${encodeURI(variable)}}}"></span>`,
					{
						updateSelection: true,
						parseOptions: {
							preserveWhitespace: "full",
						},
					}
				);
			}
			this.editor.commands.focus(this.lastCursorPosition);
		},
		updateProp(allContent) {
			let contentArray = [];
			allContent.content.forEach((paragraph) => {
				if (contentArray.length) {
					contentArray.push("\n\n");
				}
				if (paragraph.type === "paragraph" && paragraph.content) {
					paragraph.content.forEach((pieceOfContent) => {
						if (pieceOfContent.type === "text") {
							contentArray.push(pieceOfContent.text);
						} else if (pieceOfContent.type === "mention") {
							contentArray.push(decodeURI(pieceOfContent.attrs.id));
						} else if (pieceOfContent.type === "hardBreak") {
							contentArray.push("\n");
						}
					});
				} else if (paragraph.type === "codeBlock" && paragraph.content) {
					paragraph.content.forEach((pieceOfContent, i) => {
						if (i === 0) {
							contentArray.push("[[");
						}
						if (pieceOfContent.type === "text") {
							contentArray.push(pieceOfContent.text);
						} else if (pieceOfContent.type === "mention") {
							contentArray.push(decodeURI(pieceOfContent.attrs.id));
						} else if (pieceOfContent.type === "hardBreak") {
							contentArray.push("\n");
						}
						if (paragraph.content?.length - 1 === i) {
							contentArray.push("]]");
						}
					});
				}
			});
			const joinedContent = contentArray.join("");
			this.$emit("contentModified", joinedContent);
		},
		highlightVariables(content) {
			if (typeof content !== "string") {
				return content;
			}
			content = content
				.replace(/\[\[(.*?)\]\]|\{\{(.*?)\}\}/gs, (match, codeBlock, template) => {
					if (codeBlock !== undefined) {
						// Wrap the content inside [[ ]] as a JavaScript code block
						return `<pre><code class="language-js">${codeBlock.trim()}</code></pre>`;
					}
					if (template !== undefined) {
						// Replace only if NOT inside [[]]
						return `<span data-type="mention" class="mention" data-id="${encodeURI(
							`{{${template}}}`
						)}"></span>`;
					}
					return match;
				})
				.replace(/\n/g, "<br />"); // Convert new lines to <br />
			//Remove extra new line around code block
			content = content.replace(/(<br \/><br \/>)?\s*(<pre>[\s\S]*?<\/pre>)\s*(<br \/><br \/>)?/g, "$2");
			return content;
		},
	},
};
</script>
<style scoped>
@import "highlight.js/styles/github.css";

.editor /deep/ .ProseMirror pre {
	white-space: pre-wrap;
	font-size: 12px;
	border: 1px dotted deeppink;
	padding: 0px 10px 0px 10px;
	border-radius: 4px;
	overflow-x: auto;
}

.editor /deep/ .ProseMirror pre code {
	color: black;
}

.editor /deep/ .ProseMirror {
	text-align: left;
	padding-left: 10px;
	padding-right: 10px;
	border: 1px dashed #210f3c;
	border-radius: 4px;
	font-family: "Avenir", Helvetica, Arial, sans-serif !important;
	font-size: 14px;
	display: block;
	padding-top: 0px;
	padding-bottom: 0px;
	overflow-y: hidden;
}

.editor /deep/ .ProseMirror p {
	padding: 9px;
	padding-left: 5px;
	padding-right: 5px;
	border-radius: 4px;
	background: white;
	color: #606266;
	border: none;
	margin: 0px;
	line-height: normal;
	overflow: auto;
	font-weight: 500;
}

.editor /deep/ .ProseMirror:hover {
	border-color: #210f3c;
}

.editor /deep/ .ProseMirror-focused {
	outline: none;
	border: 1px solid #210f3c !important;
}

.editor /deep/ .mention {
	border: 1px solid #210f3c;
	border-radius: 0.4rem;
	padding: 0.1rem 0.3rem;
	box-decoration-break: clone;
	background-color: #210f3c;
	color: white;
	display: inline-block;
}

.editor /deep/ .ProseMirror [contenteditable="false"] {
	white-space: unset !important;
}
</style>
